import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../utils/config';
import axios from 'axios';
import './Orders.css';

const Orders = () => {
  const { user, isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Modal states
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [isAuthenticated, navigate, token]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
      setError(null);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load orders');
      setOrders([]);
      setLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/img/products/f1.jpg';
    if (imagePath.startsWith('/uploads/')) {
      return `${API_URL}${imagePath}`;
    }
    return imagePath;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ffc107',
      processing: '#17a2b8',
      shipped: '#007bff',
      delivered: '#28a745',
      cancelled: '#dc3545'
    };
    return colors[status] || '#6c757d';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: 'fa-clock',
      processing: 'fa-cog',
      shipped: 'fa-truck',
      delivered: 'fa-check-circle',
      cancelled: 'fa-times-circle'
    };
    return icons[status] || 'fa-box';
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
  };

  const closeOrderDetails = () => {
    setShowOrderDetails(false);
    setSelectedOrder(null);
    // Re-enable body scroll
    document.body.style.overflow = 'unset';
  };

  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : orders.filter(order => order.status === filterStatus);

  if (loading) {
    return (
      <div className="loading-page">
        <div className="loading-spinner"></div>
        <p>Loading orders...</p>
      </div>
    );
  }

  return (
    <div className="orders-page">
      {/* Page Header */}
      <section className="orders-header">
        <div className="orders-header-content">
          <h2>My Orders</h2>
          <p>Track and manage all your orders</p>
        </div>
      </section>

      <div className="orders-container section-p1">
        {/* Sidebar Navigation */}
        <div className="orders-sidebar">
          <div className="profile-menu">
            <Link to="/profile" className="profile-menu-item">
              <i className="fas fa-user"></i>
              <span>My Profile</span>
            </Link>
            <Link to="/orders" className="profile-menu-item active">
              <i className="fas fa-shopping-bag"></i>
              <span>My Orders</span>
            </Link>
            <Link to="/address-book" className="profile-menu-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Address Book</span>
            </Link>
          </div>
        </div>

        {/* Main Content */}
        <div className="orders-content">
          {/* Filter Tabs */}
          <div className="orders-filters">
            <button 
              className={`filter-tab ${filterStatus === 'all' ? 'active' : ''}`}
              onClick={() => setFilterStatus('all')}
            >
              <i className="fas fa-list"></i>
              All Orders ({orders.length})
            </button>
            <button 
              className={`filter-tab ${filterStatus === 'pending' ? 'active' : ''}`}
              onClick={() => setFilterStatus('pending')}
            >
              <i className="fas fa-clock"></i>
              Pending
            </button>
            <button 
              className={`filter-tab ${filterStatus === 'processing' ? 'active' : ''}`}
              onClick={() => setFilterStatus('processing')}
            >
              <i className="fas fa-cog"></i>
              Processing
            </button>
            <button 
              className={`filter-tab ${filterStatus === 'shipped' ? 'active' : ''}`}
              onClick={() => setFilterStatus('shipped')}
            >
              <i className="fas fa-truck"></i>
              Shipped
            </button>
            <button 
              className={`filter-tab ${filterStatus === 'delivered' ? 'active' : ''}`}
              onClick={() => setFilterStatus('delivered')}
            >
              <i className="fas fa-check-circle"></i>
              Delivered
            </button>
          </div>

          {/* Orders List */}
          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}

          {filteredOrders.length === 0 ? (
            <div className="no-orders">
              <div className="no-orders-icon">
                <i className="fas fa-shopping-bag"></i>
              </div>
              <h3>No Orders Found</h3>
              <p>
                {filterStatus === 'all' 
                  ? "You haven't placed any orders yet."
                  : `No ${filterStatus} orders.`
                }
              </p>
              <Link to="/shop" className="btn-shop-now">
                <i className="fas fa-shopping-cart"></i>
                Start Shopping
              </Link>
            </div>
          ) : (
            <div className="orders-list">
              {filteredOrders.map((order) => (
                <div key={order._id} className="order-card">
                  {/* Order Header */}
                  <div className="order-card-header">
                    <div className="order-info">
                      <h4>
                        <i className="fas fa-receipt"></i>
                        Order #{order.orderNumber}
                      </h4>
                      <p className="order-date">
                        <i className="far fa-calendar"></i>
                        {new Date(order.orderDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <div 
                      className="order-status" 
                      style={{ 
                        background: getStatusColor(order.status),
                        color: 'white'
                      }}
                    >
                      <i className={`fas ${getStatusIcon(order.status)}`}></i>
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="order-items">
                    {order.items.slice(0, 3).map((item, index) => (
                      <div key={index} className="order-item">
                        <img 
                          src={getImageUrl(item.image)} 
                          alt={item.name}
                        />
                        <div className="order-item-info">
                          <h5>{item.name}</h5>
                          {(item.color || item.size) && (
                            <div style={{ display: 'flex', gap: '8px', fontSize: '11px', color: '#666', margin: '2px 0' }}>
                              {item.color && <span>Color: <strong>{item.color}</strong></span>}
                              {item.size && <span>Size: <strong>{item.size}</strong></span>}
                            </div>
                          )}
                          <p>Qty: {item.quantity} × {item.price}৳</p>
                        </div>
                      </div>
                    ))}
                    {order.items.length > 3 && (
                      <div className="order-more-items">
                        +{order.items.length - 3} more items
                      </div>
                    )}
                  </div>

                  {/* Order Footer */}
                  <div className="order-card-footer">
                    <div className="order-total">
                      <span>Total Amount:</span>
                      <strong>{order.totalAmount}৳</strong>
                    </div>
                    <div className="order-actions">
                      <button 
                        onClick={() => handleViewDetails(order)}
                        className="btn-view-order"
                      >
                        <i className="fas fa-eye"></i>
                        View Details
                      </button>
                      {order.status === 'delivered' && (
                        <button className="btn-reorder">
                          <i className="fas fa-redo"></i>
                          Reorder
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Order Details Modal */}
      {showOrderDetails && selectedOrder && (
        <div className="order-details-modal" onClick={closeOrderDetails}>
          <div className="order-details-content" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="modal-header">
              <h2>
                <i className="fas fa-receipt"></i>
                Order Details
              </h2>
              <button className="modal-close" onClick={closeOrderDetails}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-body">
              {/* Order Summary */}
              <div className="details-section">
                <div className="section-title">
                  <i className="fas fa-info-circle"></i>
                  Order Summary
                </div>
                <div className="details-grid">
                  <div className="detail-item">
                    <span className="detail-label">Order Number:</span>
                    <span className="detail-value">#{selectedOrder.orderNumber}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Order Date:</span>
                    <span className="detail-value">
                      {new Date(selectedOrder.orderDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <span 
                      className="detail-status"
                      style={{ 
                        background: getStatusColor(selectedOrder.status),
                        color: 'white'
                      }}
                    >
                      <i className={`fas ${getStatusIcon(selectedOrder.status)}`}></i>
                      {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Payment Method:</span>
                    <span className="detail-value payment-method">
                      <i className="fas fa-credit-card"></i>
                      {selectedOrder.paymentMethod === 'cash' ? 'Cash on Delivery' :
                       selectedOrder.paymentMethod === 'bkash' ? 'bKash' :
                       selectedOrder.paymentMethod === 'nagad' ? 'Nagad' :
                       'Credit/Debit Card'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Courier Service (if available) */}
              {selectedOrder.courierService?.enabled && (
                <div className="details-section">
                  <div className="section-title">
                    <i className="fas fa-truck"></i>
                    Courier Service
                  </div>
                  <div className="courier-info">
                    <i className="fas fa-shipping-fast"></i>
                    <strong>{selectedOrder.courierService.courierName}</strong>
                  </div>
                </div>
              )}

              {/* Shipping Address */}
              <div className="details-section">
                <div className="section-title">
                  <i className="fas fa-map-marker-alt"></i>
                  Shipping Address
                </div>
                <div className="address-box">
                  <p className="address-name">
                    <i className="fas fa-user"></i>
                    {selectedOrder.shippingAddress.fullName}
                  </p>
                  <p className="address-phone">
                    <i className="fas fa-phone"></i>
                    {selectedOrder.shippingAddress.phone}
                  </p>
                  <p className="address-detail">
                    <i className="fas fa-home"></i>
                    {selectedOrder.shippingAddress.address}
                  </p>
                  <p className="address-detail">
                    <i className="fas fa-city"></i>
                    {selectedOrder.shippingAddress.city} - {selectedOrder.shippingAddress.postalCode}
                  </p>
                </div>
              </div>

              {/* Order Items */}
              <div className="details-section">
                <div className="section-title">
                  <i className="fas fa-box"></i>
                  Order Items ({selectedOrder.items.length})
                </div>
                <div className="details-items-list">
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="details-item-card">
                      <img 
                        src={getImageUrl(item.image)} 
                        alt={item.name}
                        onError={(e) => e.target.src = '/img/products/f1.jpg'}
                      />
                      <div className="details-item-info">
                        <h4>{item.name}</h4>
                        {(item.color || item.size) && (
                          <div style={{ display: 'flex', gap: '10px', fontSize: '12px', color: '#666', margin: '4px 0' }}>
                            {item.color && <span>🎨 Color: <strong>{item.color}</strong></span>}
                            {item.size && <span>📏 Size: <strong>{item.size}</strong></span>}
                          </div>
                        )}
                        <p className="item-price-detail">
                          {item.price}৳ × {item.quantity} = <strong>{item.price * item.quantity}৳</strong>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="details-section">
                <div className="section-title">
                  <i className="fas fa-calculator"></i>
                  Price Breakdown
                </div>
                <div className="price-breakdown">
                  {(() => {
                    const shipping = selectedOrder.shippingFee !== undefined 
                      ? selectedOrder.shippingFee 
                      : (selectedOrder.shippingAddress?.city?.toLowerCase() === 'khulna' ? 80 : 130);
                    const subtotal = selectedOrder.totalAmount - shipping;
                    return (
                      <>
                        <div className="price-row">
                          <span>Subtotal ({selectedOrder.items.length} items):</span>
                          <span>{subtotal}৳</span>
                        </div>
                        <div className="price-row">
                          <span>Shipping Fee ({selectedOrder.shippingAddress?.city || 'Unknown'}):</span>
                          <span>{shipping}৳</span>
                        </div>
                        <div className="price-row">
                          <span>Tax:</span>
                          <span>0৳</span>
                        </div>
                        <div className="price-total">
                          <span>Total Amount:</span>
                          <span>{selectedOrder.totalAmount}৳</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer">
              <button className="btn-print">
                <i className="fas fa-print"></i>
                Print Order
              </button>
              <button className="btn-close-modal" onClick={closeOrderDetails}>
                <i className="fas fa-times"></i>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;